import React, { useState, useRef, useEffect } from "react";
import { Input, Button, Space, DatePicker, message } from "antd";
import dayjs from "dayjs";
import { SearchOutlined } from "@ant-design/icons";
import GenericModal from "../../../Components/GenericModal";
import GenericTable from "../../../Components/GenericTable";
import GenericDatePicker from "../../../Components/GenericDatePicker";
import eyeIcon from "../../../Assets/Icons/eye.svg";
import mapIcon from "../../../Assets/Icons/mapPreview.svg";
import addIcon from "../../../Assets/Icons/add-btn.svg";
import { useNavigate } from "react-router-dom";
import "./style.css";
import { getSuspectedTheft } from "../../../API/User/suspectedtheft";
import { useDateFilter } from "../../../Contexts/DateFilterContext";
import MapContainerComponent from "../../../Components/MapContainer";

const SuspectedTheft = () => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const [theft, setThefts] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const { date, setDate } = useDateFilter();
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [currentLat, setCurrentLat] = useState(null);
  const [currentLng, setCurrentLng] = useState(null);

  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText("");
    confirm();
  };

  const highlightText = (text) => {
    if (!searchText) return text;
    const regex = new RegExp(`(${searchText})`, "gi");
    const parts = String(text).split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: "yellow",
            color: "#d32f2f",
            fontWeight: "bold",
          }}
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };
  const handleClick = () => {
    navigate("/add-high-risk");
  };

  const openMap = (record) => {
    const lat = parseFloat(record.latitude);
    const lng = parseFloat(record.longitude);

    if (isNaN(lat) || isNaN(lng)) {
      messageApi.warning("Location not available");
      return;
    }

    setSelectedVehicle({
      vehicle_name: record.vehicle,
      latitude: lat,
      longitude: lng,
    });

    setCurrentLat(lat);
    setCurrentLng(lng);

    setIsOpen(true);
  };

  const handleOk = () => {
    if (
      selectedVehicle?.latitude != null &&
      selectedVehicle?.longitude != null
    ) {
      const coords = `${selectedVehicle.latitude}, ${selectedVehicle.longitude}`;

      navigator.clipboard
        .writeText(coords)
        .then(() => {
          messageApi.success(`Coordinates copied: ${coords}`);
        })
        .catch((err) => {
          messageApi.error("Failed to copy coordinates.");
        });
    } else {
      messageApi.warning("No coordinates available for this vehicle.");
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const customerColumns = [
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("customer_name"),
    },
    {
      title: "Vehicle",
      dataIndex: "vehicle",
      key: "vehicle",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("vehicle"),
    },
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("company"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        if (!status) return "-";

        const display =
          status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();

        return (
          <span className={`status-badge ${status.toLowerCase()}`}>
            {display}
          </span>
        );
      },
      filters: [
        { text: "Confirmed", value: "confirmed" },
        { text: "Safe", value: "safe" },
        { text: "Unverified", value: "unverified" },
        { text: "Suspected", value: "suspected" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Location",
      key: "location",
      render: (_, record) => (
        <a onClick={() => openMap(record)}>
          <img src={mapIcon} alt="map preview" />
        </a>
      ),
    },

    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <a
          onClick={() => {
            navigate(`/view-high-risk-detail/${record.customer_name}`, {
              state: record.fullData,
            });
          }}
        >
          <img src={eyeIcon} alt="view" />
        </a>
      ),
    },
  ];

  const fetchData = async (selectedDate) => {
    try {
      setLoading(true);

      setThefts([]);

      const formattedDate = selectedDate?.format("YYYY-MM-DD");

      const response = await getSuspectedTheft(formattedDate, formattedDate);

      const raw = response?.data;
      const arr = Array.isArray(raw) ? raw : raw?.suspected_vehicles || [];

      const formattedData = arr.map((item, index) => ({
        key: index,
        customer_name: item.customer?.customer_name,
        vehicle: item.vehicle?.vehicle_name,
        company: item.company?.company_name,
        status: item.status,
        date: item.recorded_at,

        latitude: item.location?.latitude,
        longitude: item.location?.longitude,
        fullData: item,
      }));

      setThefts(formattedData);
    } catch (err) {
      messageApi.error("Failed to fetch theft data.");
      setThefts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setDate(dayjs());
  }, []);

  useEffect(() => {
    if (date) {
      fetchData(date);
    }
  }, [date]);
  return (
    <div className="suspected-theft">
      {contextHolder}
      <div className="button-wrapper">
        <div className="date-btn">
          <GenericDatePicker
            value={date}
            onChange={(val) => setDate(val ? dayjs(val) : null)}
          />
        </div>
        <div className="button">
          <Button type="default" onClick={handleClick} className="add-btn">
            <img src={addIcon} alt="add user" />
            Add Activity
          </Button>
        </div>
      </div>
      <div className="table">
        <GenericTable dataSource={theft} columns={customerColumns} />
      </div>

      <div className="modal">
        <GenericModal
          title={selectedVehicle?.vehicle_name ?? "Vehicle Name"}
          open={isOpen}
          onOk={handleOk}
          onCancel={handleCancel}
          // cancelText="Shutdown Vehicle"
          // changed the shut down to close because the data in this table is coming from the api which is fetching data from the db table of suspected theft and the shut down already occurs on these vehicles
          cancelText="Close"
          okText="Copy Coordinates"
        >
          <MapContainerComponent latitude={currentLat} longitude={currentLng} vehicle_name={selectedVehicle?.vehicle_name} />
        </GenericModal>
      </div>
    </div>
  );
};

export default SuspectedTheft;
